import { ClockIcon, Spinner, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useStudentWithAddressQuery } from '@wonderschool/common-food-program-data-access';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ShowErrors from '../../../Components/Messages/ShowError';
import { logError } from '../../../rollbar/api';
import type { StudentInformationIssueType } from '../../studentInformationSupportAPI';
import { StudentAddressForm } from './StudentAddressForm';
import type { StudentInformationRecoveryState } from './StudentInformationRecovery';
import { StudentInformationRecovery } from './StudentInformationRecovery';
import { StudentPersonalInformationProviderApi } from './StudentPersonalInformationProviderApi';

export type PersonalInformationLoadState = 'loading' | 'loaded' | 'missing' | 'error';

const STUDENT_INFORMATION_QUERY_TIMEOUT_MS = 10_000;

const sanitizedErrorName = (error: unknown) => (error instanceof Error ? error.name : 'UnknownError');

type StudentInformationLoadingMode = 'loading' | 'restoring' | 'verifying';

function StudentInformationLoading({ mode }: { mode: StudentInformationLoadingMode }) {
  const { t } = useTranslation();
  const titleKey =
    mode === 'loading'
      ? 'students.personalInfo.recovery.loadingTitle'
      : mode === 'verifying'
        ? 'students.personalInfo.recovery.verifyingTitle'
        : 'students.personalInfo.recovery.restoringTitle';
  const descriptionKey =
    mode === 'loading'
      ? 'students.personalInfo.recovery.loadingDescription'
      : mode === 'verifying'
        ? 'students.personalInfo.recovery.verifyingDescription'
        : 'students.personalInfo.recovery.restoringDescription';

  return (
    <div className="mt-8 pr-8" role="status" aria-live="polite" data-testid="student-information-loading">
      <div className="max-w-3xl rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-950">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
            <ClockIcon className="size-5 text-blue-700" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <strong className="block text-sm">{t(titleKey)}</strong>
            <p className="mb-0 mt-1 text-sm leading-5 text-blue-900">{t(descriptionKey)}</p>
            <div className="mt-3 h-1.5 w-36 overflow-hidden rounded-full bg-blue-100" aria-hidden="true">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-blue-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StudentPersonalInformationApiLoaderProps {
  selectedStudent: {
    id: string;
    marketplaceId?: number;
  };
  onLoadStateChange?: (state: PersonalInformationLoadState) => void;
  refreshToken?: number;
  canRecover?: boolean;
  recoveryState?: StudentInformationRecoveryState;
  onRetryRecovery?: () => void;
  onSendSupportRequest?: (issueType: StudentInformationIssueType, requestId: string) => Promise<{ requestId: string }>;
}

/**
 * This is a temporary wrapper component that loads from Provider API the right student using the passed in
 * Firebase ID. It can be deleted when StudentsTable loads students from the same source.
 */
export function StudentPersonalInformationApiLoader({
  selectedStudent,
  onLoadStateChange,
  refreshToken = 0,
  canRecover = false,
  recoveryState = 'idle',
  onRetryRecovery,
  onSendSupportRequest,
}: StudentPersonalInformationApiLoaderProps) {
  const { t } = useTranslation();
  const [hasQueryTimedOut, setHasQueryTimedOut] = useState(false);
  const [queryAttempt, setQueryAttempt] = useState(0);

  const {
    data,
    loading: isStudentQueryLoading,
    error: studentQueryError,
    refetch,
  } = useStudentWithAddressQuery({
    variables: {
      args: {
        firebaseId: selectedStudent.id,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const safeStudent = useMemo(
    () =>
      !!data && data.student
        ? { ...data.student, childId: data.student.childId || selectedStudent.marketplaceId }
        : undefined,
    [data, selectedStudent.marketplaceId]
  );

  useEffect(() => {
    setHasQueryTimedOut(false);
    if (!canRecover || !isStudentQueryLoading) return;

    const timeout = setTimeout(() => setHasQueryTimedOut(true), STUDENT_INFORMATION_QUERY_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [canRecover, isStudentQueryLoading, queryAttempt, refreshToken, selectedStudent.id]);

  const retryStudentQuery = useCallback(() => {
    setHasQueryTimedOut(false);
    setQueryAttempt((attempt) => attempt + 1);
    void refetch().catch((error) => {
      logError('StudentWithAddress retry failed', new Error('Student information retry failed'), {
        firebaseId: selectedStudent.id,
        errorName: sanitizedErrorName(error),
      });
    });
  }, [refetch, selectedStudent.id]);

  // Surface query failures to Rollbar so backend regressions are visible immediately
  // instead of looking like the original silent CCMS-7520 (missing personal info) symptom.
  useEffect(() => {
    if (studentQueryError) {
      logError('StudentWithAddress query failed', new Error('Student information query failed'), {
        firebaseId: selectedStudent.id,
        graphQLErrors: studentQueryError.graphQLErrors?.map((e) => ({
          path: e.path,
          code: e.extensions?.code,
        })),
        networkErrorName: sanitizedErrorName(studentQueryError.networkError),
      });
    }
  }, [studentQueryError, selectedStudent.id]);

  // Derive the "Postgres row is missing for this student" condition as a single
  // primitive so the useEffect below depends on a stable boolean (not Apollo's
  // `data` reference, which can churn on cache updates) — keeps the Rollbar log
  // firing exactly once per affected student.
  const isPostgresStudentMissing =
    !isStudentQueryLoading && !studentQueryError && !hasQueryTimedOut && !!data && !data.student;

  const onLoadStateChangeRef = useRef(onLoadStateChange);
  const previousRefreshTokenRef = useRef(refreshToken);
  const previousStudentIdRef = useRef(selectedStudent.id);

  useEffect(() => {
    onLoadStateChangeRef.current = onLoadStateChange;
  }, [onLoadStateChange]);

  useEffect(() => {
    if (selectedStudent.id !== previousStudentIdRef.current) {
      previousStudentIdRef.current = selectedStudent.id;
      previousRefreshTokenRef.current = refreshToken;
      return;
    }
    if (refreshToken === previousRefreshTokenRef.current) return;
    previousRefreshTokenRef.current = refreshToken;
    void refetch().catch((error) => {
      logError('StudentWithAddress verification refetch failed', new Error('Student verification refetch failed'), {
        firebaseId: selectedStudent.id,
        errorName: sanitizedErrorName(error),
      });
    });
  }, [refetch, refreshToken, selectedStudent.id]);

  useEffect(() => {
    if (!selectedStudent.id) return;

    if (hasQueryTimedOut) {
      onLoadStateChangeRef.current?.('error');
    } else if (isStudentQueryLoading) {
      onLoadStateChangeRef.current?.('loading');
    } else if (studentQueryError) {
      onLoadStateChangeRef.current?.('error');
    } else if (!safeStudent) {
      onLoadStateChangeRef.current?.('missing');
    } else {
      onLoadStateChangeRef.current?.('loaded');
    }
  }, [canRecover, hasQueryTimedOut, isStudentQueryLoading, safeStudent, selectedStudent.id, studentQueryError]);

  // When the query resolves cleanly but data.student is null, the Postgres row for
  // this Firebase ID is missing or unlinked — the CCMS-7520 sync-race / missing-link
  // class of failure. Engineering needs to repair the affected student via the
  // backfill job; surface it to Rollbar so ops finds out before support tickets.
  useEffect(() => {
    if (isPostgresStudentMissing) {
      logError(
        'StudentWithAddress query returned null student',
        new Error('StudentWithAddress query returned null student'),
        {
          firebaseId: selectedStudent.id,
        }
      );
    }
  }, [isPostgresStudentMissing, selectedStudent.id]);

  if (!selectedStudent.id) return null;

  if (studentQueryError || hasQueryTimedOut) {
    if (!canRecover) {
      return (
        <div className="mt-8 pr-8">
          <ShowErrors content={t('students.personalInfo.loadError')} />
        </div>
      );
    }

    return (
      <div className="mt-8 pr-8">
        <StudentInformationRecovery
          variant="load-error"
          onRetry={retryStudentQuery}
          onSendSupportRequest={onSendSupportRequest}
        />
      </div>
    );
  }

  if (isStudentQueryLoading) {
    if (!canRecover) {
      return <Spinner size={WidgetSizeEnum.X_LARGE} spinnerColor="purple" className="m-10" />;
    }

    return <StudentInformationLoading mode={recoveryState === 'verifying' ? 'verifying' : 'loading'} />;
  }

  if (!safeStudent) {
    if (!canRecover || !onRetryRecovery) {
      return (
        <div className="mt-8 pr-8">
          <ShowErrors content={t('students.personalInfo.loadError')} />
        </div>
      );
    }

    if (recoveryState === 'error') {
      return (
        <div className="mt-8 pr-8">
          <StudentInformationRecovery
            variant="repair-error"
            onRetry={onRetryRecovery}
            onSendSupportRequest={onSendSupportRequest}
          />
        </div>
      );
    }

    return <StudentInformationLoading mode={recoveryState === 'verifying' ? 'verifying' : 'restoring'} />;
  }

  // Left padding of pr-8 is required to align with legacy components - they can be removed while every part of
  // student form uses the same common components.
  return (
    <div className="pr-8">
      <StudentPersonalInformationProviderApi student={safeStudent} />
      <StudentAddressForm student={safeStudent} address={safeStudent.address || null} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  selectedStudent: state.students.selectedStudent,
});

// We are temporarily connecting with Redux here. The trick is that this file can be thrown away
// when StudentsTable loads all data from Postgres and passes the loaded student directly to the component
// holding the form.
export const StudentPersonalInformationForm = connect(mapStateToProps)(StudentPersonalInformationApiLoader);
